import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Form, Modal, Placeholder } from 'react-bootstrap';
import { useTreeStore } from '@/frontend/store/TreeStore';
import { I18n } from '@/i18n/I18n';
import { OperationButtonRow } from '../operations/OperationButtonRow';
import { CollapsibleAddRow } from '../buttons/CollapsibleAddRow';
import { OperationItem } from '../operations/OperationItem';
import { BasicContainer } from './BasicContainer';
import { ContainerTreeObject } from '@/frontend/types/navigationTypes';

export function Scenario(props: { scenario: ContainerTreeObject, parentShowCascade?: boolean }) {
  const { scenario, parentShowCascade } = props;
  const { container } = scenario;
  const containerItem = useTreeStore((state) => { return state.containerLookup[scenario.id]; });
  const operations = useTreeStore(useShallow((state) => state.getStoreOperationsForParent(scenario.id)));
  const [showItem, setShowItem] = useState<boolean>(parentShowCascade === undefined ? true : parentShowCascade);
  const updateContainer = useTreeStore((state) => state.updateContainer);
  const [title, setTitle] = useState(container.title);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const containerRef = useRef(null);
  const [description, setDescription] = useState(container.description);
  const timeoutRef = useRef<number>(null);
  const procedureLoaded = useTreeStore((state) => state.procedureLoaded);
  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      void updateContainer({ container: { ...container, description: value } });
    }, 300);
  };

  useEffect(() => {
    if (!procedureLoaded) {
      useTreeStore.setState({ procedureLoaded: true });
    }
  }, [procedureLoaded]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // const handleOllamaComplete = (response: string) => {
  //   setDescription(response);
  //   updateContainer({ container: { ...container, description: response } });
  // };

  useEffect(() => {
    setShowItem(parentShowCascade === undefined ? true : parentShowCascade);
  }, [parentShowCascade]);

  // Re-sync from the store when the saved title changes (external updates, or our own
  // blur commit echoing back). Safe because we only commit on blur, so container.title
  // is stable while the user is typing.
  useEffect(() => {
    setTitle(container.title);
  }, [container.title]);

  const commitTitle = () => {
    if (!title.trim()) {
      setTitle(container.title); // revert: never leave/commit a blank title
      return;
    }
    if (title === container.title) return;
    void updateContainer({ container: { ...container, title } });
  };

  if (!containerItem?.loaded) {
    return (
      <div className="card-header bg-body-secondary" aria-label='scenario loading' id={scenario.id}>
        <BasicContainer container={container} showSection={showItem} setShowSection={setShowItem} />
        <Placeholder data-testid="loadingIndicator" className='d-flex flex-column pt-3 observed-element' animation='glow'>
          <Placeholder.Button xs={12} size='lg' className='btn btn-secondary p-3' outline='true' />
          <div className='d-flex justify-content-between'>
            <Placeholder.Button xs={5} size='lg' className='my-4 p-3 btn btn-secondary' />
            <Placeholder.Button xs={5} size='lg' className='my-4 p-3 btn btn-secondary' />
          </div>
          <div className='d-flex justify-content-between'>
            <Placeholder.Button xs={12} size='lg' className='my-4 p-3 btn btn-secondary' />
          </div>
        </Placeholder>
        {operations.map((operation) => <div id={operation.id} key={operation.id} />)}
      </div>
    );
  }

  const collapsedClass = showItem ? '' : 'border-bottom-0';

  return (
    <div id={container.id} className="card bg-light border border-dark-subtle observed-element mt-2">
      <div className={`card-header bg-body-secondary d-flex flex-column ${collapsedClass}`} data-testid='scenario-item-header'>
        <Form.Label className="col-form-label col-form-label-sm">{I18n.get('Title')}</Form.Label>
        <Form.Control
          type="text"
          size='sm'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
        />
        <button
          type="button"
          className="btn btn-sm btn-primary overflow-hidden mt-2"
          onClick={() => setShowDescriptionModal(true)}
          aria-label={I18n.get('editDescription')}
          style={{ maxHeight: '32px', width: 'fit-content' }}
        >
          {I18n.get('editDescription')}
        </button>
        <Modal show={showDescriptionModal} onHide={() => setShowDescriptionModal(false)}>
          <Modal.Header closeButton>
            <div className="d-flex flex-column  gap-2 w-100">
              <Modal.Title>{I18n.get('editTypeDescription', { type: I18n.get(container.containerType.charAt(0).toUpperCase() + container.containerType.slice(1)) })}</Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-column gap-2" ref={containerRef}>
              <textarea
                className="form-control"
                id={`description-${container.id}`}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                value={description}
                placeholder={I18n.get('description')}
                style={{ minHeight: '12em', resize: 'vertical' }}
                aria-label={I18n.get('description')}
              />
              {/* <div className="d-flex justify-content-end">
                <OllamaChatGenerator
                  onComplete={handleOllamaComplete}
                  buttonText={I18n.get('ollamaGenerateChat')}
                  buttonClassName="btn-sm"
                  containerRef={containerRef}
                />
              </div> */}
            </div>
          </Modal.Body>
        </Modal>
      </div>
      <div className="card-body pt-3" hidden={!showItem} data-testid='scenario-item-body'>
        <div className="row">
          {operations.length === 0 && <div className='text-muted py-3'>{I18n.get('noOperations')}</div>}
          <CollapsibleAddRow label={I18n.get('addOperationHere')} defaultOpen={operations.length === 0}>
            <OperationButtonRow position={0} parent={container} />
          </CollapsibleAddRow>
          <div className="col">
            {operations.map((operation) => {
              return (
                <div key={`$operation-${operation.id}`}>
                  <OperationItem
                    key={scenario.id}
                    initItem={operation}
                    parent={container}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
